import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Card, Snackbar, Alert, Button } from '@mui/material'; // Added Button
import { styled } from '@mui/material/styles'; // Added styled
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { OrgContext } from 'context/orgContext';
import StageEnvironment from './components/StageEnvironment';
import StageCommit from './components/StageCommit';
import StageReview from './components/StageReview';
import StageProduct from './components/StageProduct';
import { ProductContext } from 'context/productContext';
import { UserContext } from 'context/userContext';
import { userOrgs } from 'services/org';
import { getSalesforceProducts, getAllVersionsForPatch, getProductsFromDb } from 'services/product';
import { useNotifications } from 'context/NotificationContext'; // Import notification hook
import { areProductsSameEntity } from './utils/productDataHelper';

const NextButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

function Commit() {
  const [activeStage, setActiveStage] = useState('environment');
  const [renderStages, setRender] = useState(false); // Initialize to false
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const isInitialMount = useRef(true);
  const [localUserId, setLocalUserId] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [removedProducts, setRemovedProducts] = useState([]);
  const [productFields, setProductFields] = useState([]);
  const [modifiedProducts, setModifiedProducts] = useState([]);
  const [commitProducts, setCommitProducts] = useState([]);
  const [versionType, setVersionType] = useState('');
  const [sfProducts, setSfProducts] = useState([]);
  const [majorVersionProducts, setMajorVersionProducts] = useState([]);
  const [finalSelectedMajorVersions, setFinalSelectedMajorVersions] = useState([]);
  const [parentDatabaseProducts, setDatabaseProducts] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isRevenue, setIsRevenue] = useState('');
  const [isReviewCompleted, setIsReviewCompleted] = useState(false);

  // Snackbar and Notification states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const { addNotification } = useNotifications();

  const handleMajorVersionsSelected = useCallback((selectedVersions) => {
    console.log('[handleMajorVersionsSelected] selectedVersions', selectedVersions);
    setFinalSelectedMajorVersions(selectedVersions);
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleVersionTypeChange = (newVersionType) => {
    if (newVersionType !== 'patch') {
      setMajorVersionProducts([]);
      setFinalSelectedMajorVersions([]);
    } else {
      setDatabaseProducts([]);
    }
    setVersionType(newVersionType);
  };

  const handleStageChange = (stage) => {
    if (stage !== 'environment' && (!selectedOrg || !versionType)) {
      const message = 'Please select an environment and version type before proceeding.';
      showSnackbar(message, 'warning');
      return;
    }

    if (stage === 'review' && selectedProducts.length === 0) {
      const message = 'You must select at least one product before proceeding to the review stage.';
      showSnackbar(message, 'warning');
      return; // Block navigation
    }

    if (stage === 'commit' && !isReviewCompleted) {
      const message = 'You must complete the "Review Changes" stage before committing.';
      showSnackbar(message, 'warning');
      return; // Block navigation to the commit stage
    }

    setActiveStage(stage);
  };

  const selectedEnv = (orgObject) => {
    if (!orgObject) {
      setSelectedOrg(null);
      setVersionType('');
      setMajorVersionProducts([]);
      setFinalSelectedMajorVersions([]);
      setSfProducts([]);
      setProductFields([]);
      return;
    }

    const selected = orgObject;

    console.log('[selectedEnv] - selected', selected);

    setIsRevenue(selected.type);
    setSelectedOrg(selected);
  };

  useEffect(() => {
    setLocalUserId(userId);
  }, [userId]);

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!userId) return; // Don't fetch if userId isn't set from context
      try {
        const { orgs: fetchedOrgs } = await userOrgs();
        if (fetchedOrgs) {
          setOrgs(fetchedOrgs);
        }
      } catch (error) {
        const errorMsg = `Error fetching organizations: ${error.message}`;
        showSnackbar(errorMsg, 'error');
      } finally {
        setRender(true); // Allow rendering stages now
      }
    };

    if (!orgs || orgs.length === 0) {
      fetchOrgs();
    } else {
      setRender(true); // Orgs are already in context, just render.
    }
  }, [userId, orgs, setOrgs, addNotification]);

  useEffect(() => {
    // This effect runs whenever the list of selected products changes.
    if (selectedProducts.length === 0) {
      // If the user has cleared their selections, they are no longer
      // considered to have completed the review stage.
      setIsReviewCompleted(false);
    }
  }, [selectedProducts]);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedOrg || !selectedOrg.org_id || !localUserId || !versionType) {
        // Ensure all dependencies are ready
        setSfProducts([]);
        setMajorVersionProducts([]);
        setDatabaseProducts([]);
        return;
      }

      console.log('[fetchData] - selectedOrg', selectedOrg);

      try {
        if (versionType === 'patch') {
          // Fetch ALL product versions from DB (not just major versions X.0.0)
          // This ensures we show all versions in the dropdown: 1.0.0, 1.0.1, 1.1.0, 2.0.0, etc.
          const allVersionsResponse = await getAllVersionsForPatch(selectedOrg.org_id, []);
          const fetchedAllVersions = allVersionsResponse?.products || [];
          if (allVersionsResponse?.error || !allVersionsResponse?.products) {
            console.warn('All versions fetch failed:', allVersionsResponse?.message || 'Could not fetch product versions.');
          }

          const sfProductsResponse = await getSalesforceProducts(selectedOrg.org_id);
          const fetchedSfProducts = sfProductsResponse?.products || [];
          if (sfProductsResponse?.error || !sfProductsResponse?.products) {
            console.warn(
              'Salesforce products fetch failed:',
              sfProductsResponse?.message || 'Could not fetch Salesforce products.'
            );
          }

          console.log('[COMMIT DEBUG] Fetched All Versions from DB:', fetchedAllVersions.length);
          console.log('[COMMIT DEBUG] Sample DB version:', fetchedAllVersions[0]);
          console.log('[COMMIT DEBUG] Fetched SF Products:', fetchedSfProducts.length);
          console.log('[COMMIT DEBUG] Sample SF product:', fetchedSfProducts[0]);
          
          // COMMIT-SPECIFIC: Filter to only show products from the SELECTED org (not all orgs of same type)
          const versionsFromSelectedOrg = fetchedAllVersions.filter((dbProduct) => 
            dbProduct.org_id === selectedOrg.org_id
          );
          console.log('[COMMIT DEBUG] Versions from selected org only:', versionsFromSelectedOrg.length);
          
          // Filter DB versions to only include products that exist in Salesforce
          // Uses areProductsSameEntity to handle property name variations (sf_id vs sfId, etc.)
          const intersectedVersions = versionsFromSelectedOrg.filter((dbProduct) => {
            const hasMatch = fetchedSfProducts.some((sfProduct) => areProductsSameEntity(dbProduct, sfProduct));
            if (!hasMatch) {
              console.log('[COMMIT DEBUG] No SF match for DB product:', {
                name: dbProduct.name,
                version: dbProduct.product_version,
                sf_id: dbProduct.sf_id,
                external_id: dbProduct.external_id,
                id: dbProduct.id,
                org_id: dbProduct.org_id
              });
            }
            return hasMatch;
          });
          
          console.log('[COMMIT DEBUG] Intersected Versions:', intersectedVersions.length);
          console.log('[COMMIT DEBUG] Sample intersected:', intersectedVersions[0]);

          // Filter SF products to only include those that have versions in the database
          const filteredSfProducts = fetchedSfProducts.filter((sfProduct) =>
            intersectedVersions.some((dbProduct) => areProductsSameEntity(dbProduct, sfProduct))
          );

          setMajorVersionProducts(intersectedVersions);
          setSfProducts(filteredSfProducts);
          if (intersectedVersions.length === 0 || filteredSfProducts.length === 0) {
            console.info('No products found matching between Salesforce and existing product versions for patching.');
          }
        } else {
          // For 'new' version type
          const sfProductsResponse = await getSalesforceProducts(selectedOrg.org_id);
          const fetchedSfProducts = sfProductsResponse?.products || [];
          setSfProducts(fetchedSfProducts);
          if (sfProductsResponse?.error || !sfProductsResponse?.products) {
            console.warn(
              'Salesforce products fetch failed:',
              sfProductsResponse?.message || 'Could not fetch Salesforce products.'
            );
          } else if (fetchedSfProducts.length === 0) {
            console.info('No products found in the selected Salesforce environment.');
          }

          let db = await getProductsFromDb(selectedOrg.org_id, []);
          const fetchedDbProducts = db?.products || [];
          setDatabaseProducts(fetchedDbProducts);
          if (db?.error || !db?.products) {
            console.warn(
              'Database products fetch failed:',
              db?.message || 'Could not fetch existing database products.'
            );
          }
        }
      } catch (error) {
        const errorMsg = `Error fetching product data: ${error.message}`;
        addNotification({ title: ['Error', 'Product Fetch Error'], severity: 'error', details: errorMsg });
        showSnackbar(errorMsg, 'error');
        setSfProducts([]);
        setMajorVersionProducts([]);
        setDatabaseProducts([]);
      }
    };

    // Fetch data when:
    // 1. Not on initial mount
    // 2. User navigates to 'products' stage (to ensure fresh data)
    // 3. OR when any dependency changes
    const shouldFetch = !isInitialMount.current && (activeStage === 'products' || activeStage === 'environment');
    
    if (shouldFetch) {
      fetchData();
    } else if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [localUserId, selectedOrg, versionType, activeStage, addNotification]); // Added activeStage

  useEffect(() => {
    if (activeStage === 'environment') {
      setSelectedProducts([]);
      setRemovedProducts([]);
      setFinalSelectedMajorVersions([]); // Reset major version selections too
      setModifiedProducts([]);
      setCommitProducts([]);
    }
  }, [activeStage]);

  const handleModifiedProducts = useCallback((updatedProducts) => {
    setModifiedProducts(updatedProducts);
    // If we receive valid products from the review stage, it means the stage is complete.
    if (updatedProducts && updatedProducts.length > 0) {
      setIsReviewCompleted(true);
    } else {
      // If the review results in no products to modify, it's also "complete" but there's nothing to commit.
      // We can still mark it as complete to allow the user to see the empty commit stage if we want.
      setIsReviewCompleted(true);
    }
  }, []);

  const renderStageContent = () => {
    switch (activeStage) {
      case 'environment':
        return (
          <StageEnvironment
            envs={orgs || []} // Pass orgs or empty array
            selected={selectedOrg}
            selectedEnv={selectedEnv}
            onVersionTypeChange={handleVersionTypeChange}
            initialVersionType={versionType}
          />
        );
      case 'products':
        // Ensure required props are available before rendering
        if (!selectedOrg || !versionType)
          return <ArgonTypography>Please select environment and version type first.</ArgonTypography>;
        return (
          <StageProduct
            variants={sfProducts}
            selectedVariants={setSelectedProducts}
            initialSelected={selectedProducts}
            versionType={versionType}
            majorVersionProducts={majorVersionProducts}
            onMajorVersionsSelected={handleMajorVersionsSelected}
            parentDatabaseProducts={parentDatabaseProducts}
            initialMajorVersions={finalSelectedMajorVersions}
          />
        );
      case 'review':
        if (selectedProducts.length === 0)
          return <ArgonTypography>Please select environment, version type, and products first.</ArgonTypography>;
        return (
          <StageReview
            userId={userId}
            org={selectedOrg}
            orgId={selectedOrg.org_id}
            isRevenue={isRevenue === 'RC'}
            productFields={productFields}
            selectedProducts={selectedProducts}
            onModifiedProducts={handleModifiedProducts}
            databaseMajorVersions={finalSelectedMajorVersions}
            setCommitProducts={setCommitProducts}
            versionType={versionType}
          />
        );
      case 'commit':
        if (!isReviewCompleted) {
          return <ArgonTypography>Please complete the review stage first.</ArgonTypography>;
        }
        return (
          <StageCommit
            userId={userId}
            org={selectedOrg}
            selectedProducts={modifiedProducts}
            removedProducts={removedProducts}
            commitProducts={commitProducts}
            handleStageChange={handleStageChange}
            versionType={versionType}
          />
        );
      default:
        return null;
    }
  };

  const renderNavigation = () => {
    const commonButtonStyles = {
      mt: 3,
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
    };

    switch (activeStage) {
      case 'environment':
        return (
          <ArgonBox sx={{ ...commonButtonStyles, justifyContent: 'flex-end' }}>
            <NextButton
              variant="contained"
              onClick={() => handleStageChange('products')}
              disabled={!selectedOrg || !versionType}
            >
              Select Products &rarr;
            </NextButton>
          </ArgonBox>
        );
      case 'products':
        return (
          <ArgonBox sx={commonButtonStyles}>
            <BackButton variant="contained" onClick={() => handleStageChange('environment')}>
              &larr; Select Environment
            </BackButton>
            <NextButton
              variant="contained"
              onClick={() => handleStageChange('review')}
              disabled={selectedProducts.length === 0}
            >
              Review Changes &rarr;
            </NextButton>
          </ArgonBox>
        );
      case 'review':
        return (
          <ArgonBox sx={commonButtonStyles}>
            <BackButton variant="contained" onClick={() => handleStageChange('products')}>
              &larr; Select Products
            </BackButton>
            <NextButton variant="contained" onClick={() => handleStageChange('commit')} disabled={!isReviewCompleted}>
              Proceed to Commit &rarr;
            </NextButton>
          </ArgonBox>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3} flexWrap="wrap">
              <ArgonTypography
                variant="button"
                color={activeStage === 'environment' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('environment')}
                sx={{ cursor: 'pointer' }}
              >
                Select Environment
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'products' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('products')}
                sx={{
                  cursor: selectedOrg && versionType ? 'pointer' : 'default',
                  opacity: selectedOrg && versionType ? 1 : 0.5,
                }}
              >
                Select Products
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'review' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('review')}
                sx={{
                  // --- VISUALLY DISABLE THE REVIEW LINK ---
                  cursor: selectedProducts.length > 0 ? 'pointer' : 'not-allowed',
                  opacity: selectedProducts.length > 0 ? 1 : 0.5,
                }}
              >
                Review Changes
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'commit' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('commit')}
                sx={{
                  // The logic for this link is already correct
                  cursor: isReviewCompleted ? 'pointer' : 'not-allowed',
                  opacity: isReviewCompleted ? 1 : 0.5,
                }}
              >
                Commit Changes
              </ArgonTypography>
            </ArgonBox>
          </Card>
          {renderStages && renderStageContent()}
          {renderStages && renderNavigation()}
        </ArgonBox>
      </ArgonBox>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Changed anchor to top
      >
        <Alert
          onClose={handleSnackbarClose}
          variant="filled"
          severity={snackbarSeverity}
          sx={{ alignItems: 'center', alignContent: 'center' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Commit;
